/* eslint-disable no-loop-func */
import { v4 as uuidv4 } from "uuid";
import { IOption } from "../types/CommonTypes";
import { IContact } from "../types/Contact";
import { ILogicValues, IRecipientTag } from "../types/SavedSearchTypes";
import {
  INamedTagQuery,
  IOptionValues,
  ITagQuery,
} from "../types/TagQueryTypes";
import { ITag } from "../types/TagTypes";

function isOption(
  value: IOptionValues | ILogicValues | undefined
): value is IOptionValues {
  return (
    value === "ANY_OF" ||
    value === "NONE_OF" ||
    value === "ALL" ||
    value === "ALL_OF"
  );
}

const OPTIONS_FILTER: IOption[] = [
  {
    value: "ANY_OF",
    name: "With any of these tags",
  },
  {
    value: "ALL_OF",
    name: "With all of these tags",
  },
  {
    value: "NONE_OF",
    name: "None of these tags",
  },
];

const LOGIC: { [key: string]: string } = {
  ["AND"]: "And",
  ["OR"]: "Or",
};

export function recipientToTagQuery(recipientTags: IRecipientTag[]): ITagQuery {
  const auxRecipientTags = [...recipientTags];

  const lastNode = auxRecipientTags.pop() as IRecipientTag;
  let obj: ITagQuery = {
    operator: isOption(
      lastNode.option.value as IOptionValues | ILogicValues | undefined
    )
      ? (lastNode.option.value as IOptionValues)
      : "ANY_OF",
    tags: lastNode.tags.map((tag) => tag._id!),
    next: undefined,
  };

  while (auxRecipientTags.length) {
    const lastRecipientTag = auxRecipientTags[auxRecipientTags.length - 1];

    const secondLastNode: ITagQuery = {
      tags: lastRecipientTag.tags.map((tag) => tag._id!),
      operator: isOption(
        lastRecipientTag.option.value as
          | IOptionValues
          | ILogicValues
          | undefined
      )
        ? (lastRecipientTag.option.value as IOptionValues)
        : "ANY_OF",
      next: {
        type: isOption(
          lastRecipientTag.logic.value as
            | IOptionValues
            | ILogicValues
            | undefined
        )
          ? "AND"
          : (lastRecipientTag.logic.value as ILogicValues),
        query: obj,
      },
    };
    obj = secondLastNode;
    auxRecipientTags.pop();
  }
  return obj;
}

export function recipientToNamedTagQuery(
  recipientTags: IRecipientTag[]
): INamedTagQuery {
  const auxRecipientTags = [...recipientTags];

  const lastNode = auxRecipientTags.pop() as IRecipientTag;
  let obj: INamedTagQuery = {
    operator: isOption(
      lastNode.option.value as IOptionValues | ILogicValues | undefined
    )
      ? (lastNode.option.value as IOptionValues)
      : "ANY_OF",
    tags: lastNode.tags.map((tag) => ({
      id: tag._id!,
      name: tag.name,
    })),
    next: undefined,
  };

  while (auxRecipientTags.length) {
    const lastRecipientTag = auxRecipientTags[auxRecipientTags.length - 1];

    const secondLastNode: INamedTagQuery = {
      tags: lastRecipientTag.tags.map((tag) => ({
        id: tag._id!,
        name: tag.name,
      })),
      operator: isOption(
        lastRecipientTag.option.value as
          | IOptionValues
          | ILogicValues
          | undefined
      )
        ? (lastRecipientTag.option.value as IOptionValues)
        : "ANY_OF",
      next: {
        type: isOption(
          lastRecipientTag.logic.value as
            | IOptionValues
            | ILogicValues
            | undefined
        )
          ? "AND"
          : (lastRecipientTag.logic.value as ILogicValues),
        query: obj,
      },
    };
    obj = secondLastNode;
    auxRecipientTags.pop();
  }
  return obj;
}

export function tagQueryToRecipient(tagQuery: ITagQuery, tags: ITag[]) {
  let currentTagQuery: ITagQuery = tagQuery;
  let recipientTag: IRecipientTag[] = [];

  while (currentTagQuery) {
    const newTags = tags.filter((t) => {
      return currentTagQuery.tags.find((tt) => tt === t._id.toString());
    });

    const recipient = {
      option: {
        name: OPTIONS_FILTER.find((o) => o.value === currentTagQuery.operator)
          .name,
        value: currentTagQuery.operator,
      },
      logic: {
        value: currentTagQuery.next ? currentTagQuery.next.type : "AND",
        name: currentTagQuery.next ? LOGIC[currentTagQuery.next.type] : "And",
      },
      tags: newTags,
      id: uuidv4(),
    };

    recipientTag.push(recipient);

    currentTagQuery = currentTagQuery.next?.query;
  }
  return recipientTag;
}

interface IQuery {
  type: "AND" | "OR" | undefined;
  contacts: IContact[];
}

export const tagQueryToContacts = (
  allContacts: IContact[],
  tagQuery: ITagQuery
) => {
  // Aux array to iterate between tag queries
  let actualQuery: ITagQuery | undefined = { ...tagQuery };

  // Array with partially filtered users
  const queryArray: IQuery[] = [];

  /*
   * Generate an array of filtered contact arrays
   * from all tag queries
   */
  while (actualQuery) {
    const array = allContacts.filter((contact) => {
      // Tags defined by the user
      const { tags } = contact;

      // There are two operators: any of values anD none of values
      switch (actualQuery?.operator) {
        case "ALL":
          return true;
        case "ANY_OF":
          if (!tags) return false;
          return tags.find((val) =>
            actualQuery?.tags.find((id) => id === val._id)
          );
        case "NONE_OF":
          // Returns true if no value of actualQuery.tags matches with tagIds
          if (!tags) return true;
          return tags.every((val) =>
            actualQuery?.tags.find((id) => id !== val._id)
          );
        case "ALL_OF":
          // Returns true if no value of actualQuery.tags matches with tagIds
          if (!tags) return false;
          return actualQuery?.tags.every((val) =>
            tags.find(({ _id }) => _id === val)
          );
        default:
          return false;
      }
    });

    /*
     * Adds the filtered contacts to the query array as
     * well as the next type (OR or AND)
     */
    queryArray.push({ type: actualQuery.next?.type, contacts: array });

    // Change the aux iterator to the next tag query
    actualQuery = actualQuery.next?.query;
  }

  // Array to store the result of all queries
  let filteredContacts: IContact[] = [];

  // Aux variable to store the next type (AND or OR)
  let nextType: "AND" | "OR" | undefined = undefined;

  /*
   * Since we have all filtered arrays, we must make the AND and OR
   * operations, it can be achieved by making set operations
   */
  queryArray.forEach((query) => {
    if (!nextType) {
      // In the first iteration, add the first array to the filteredContacts
      filteredContacts = [...query.contacts];
    } else {
      switch (nextType) {
        case "AND":
          // Make an intersection operation with filteredContacts and actual array
          filteredContacts = filteredContacts.filter((val) =>
            query.contacts.includes(val)
          );
          break;
        case "OR":
          // Make a union operation with filteredContacts and actual array
          filteredContacts = Array.from(
            new Set([...filteredContacts, ...query.contacts])
          );
          break;
        default:
          break;
      }
    }
    // Save the type (AND or OR) for the next operation
    nextType = query.type;
  });
  return filteredContacts;
};

export const tagsOnTagQuery = (tagQuery: ITagQuery): string[] => {
  let tempTagQuery: ITagQuery = { ...tagQuery };
  const allTags: string[] = [];

  allTags.push(...tempTagQuery.tags);

  while (tempTagQuery.next) {
    tempTagQuery = tempTagQuery.next.query;
    allTags.push(...tempTagQuery.tags);
  }

  return allTags;
};
